/**
 * CatalogCache — IndexedDB cache for catalog data (movies, shows, seasons, games, etc.).
 *
 * Catalog data rarely changes (only when new items are added). This cache stores it
 * locally so subsequent page loads can skip the full server fetch and only request
 * user-specific data.
 *
 * Each app type gets its own cache store. The cache is keyed by app type and stores:
 *   - items: the full catalog array
 *   - timestamp: when the cache was written (ISO string)
 *
 * Usage:
 *   const cache = new CatalogCache('movies');
 *   const cached = await cache.get();
 *   if (cached) { use cached.items; }
 *   else { fetch from server; await cache.set(items); }
 */

const DB_NAME = 'tracker-catalog-cache';
const DB_VERSION = 1;
const STORE_NAME = 'catalogs';
const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes (aligned with server-side catalog cache TTL)

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

export default class CatalogCache {
    constructor(appType) {
        this._key = appType;
    }

    /**
     * Get cached catalog data if it exists and isn't too old.
     * @returns {Promise<{ items: any[], timestamp: string } | null>}
     */
    async get() {
        try {
            const db = await openDB();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readonly');
                const store = tx.objectStore(STORE_NAME);
                const req = store.get(this._key);
                req.onsuccess = () => {
                    const entry = req.result;
                    if (!entry || !entry.timestamp) return resolve(null);
                    const age = Date.now() - new Date(entry.timestamp).getTime();
                    if (age > MAX_AGE_MS) return resolve(null); // stale
                    resolve(entry);
                };
                req.onerror = () => resolve(null);
            });
        } catch {
            return null;
        }
    }

    /**
     * Get cached data even if expired, with a staleness flag.
     * Returns { items, timestamp, isStale } or null if nothing cached.
     */
    async getEvenIfStale() {
        try {
            const db = await openDB();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readonly');
                const store = tx.objectStore(STORE_NAME);
                const req = store.get(this._key);
                req.onsuccess = () => {
                    const entry = req.result;
                    if (!entry || !entry.timestamp) return resolve(null);
                    const age = Date.now() - new Date(entry.timestamp).getTime();
                    resolve({ ...entry, isStale: age > MAX_AGE_MS });
                };
                req.onerror = () => resolve(null);
            });
        } catch {
            return null;
        }
    }

    /**
     * Store catalog data in the cache.
     * @param {any[]} items - Catalog items (without _userData)
     */
    async set(items) {
        try {
            const db = await openDB();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                store.put({ items, timestamp: new Date().toISOString() }, this._key);
                tx.oncomplete = () => resolve();
                tx.onerror = () => resolve();
            });
        } catch {
            // Cache write failure is non-fatal
        }
    }

    /**
     * Clear the cache for this app type.
     */
    async clear() {
        try {
            const db = await openDB();
            return new Promise((resolve) => {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                store.delete(this._key);
                tx.oncomplete = () => resolve();
                tx.onerror = () => resolve();
            });
        } catch {
            // Ignore
        }
    }
}
